import { useEffect, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Switch, FormControlLabel,
  Button, Alert, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import {
  getMacieConfig, setMacieDiscovery, updateMacieRegions, disableMacie,
  startMacieRolesScan, getMacieRolesJob, fixMacieRoles,
} from '../../../services/macieApi';
import RolePermissionsPanel from '../../../components/portal/securityhub/RolePermissionsPanel';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import MultiSelectField from './MultiSelectField';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading } from '../../../components/portal/PortalStates';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, listCardSx, sectionCardSx, sectionHeadingSx, dialogProps, dialogTitleSx,
} from '../portalStyles';

const MACIE_COLOR = '#0d9488';
const MACIE_COLOR_DARK = '#0B7C72';

const macieButtonSx = {
  backgroundColor: MACIE_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: MACIE_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };

const settingsHeader = (
  <PortalPageHeader
    icon={<SettingsIcon sx={{ fontSize: 24 }} />}
    title="Settings"
    subtitle="Manage sensitive-data discovery, linking regions, and Macie status"
    color={MACIE_COLOR}
  />
);

// Macie's GetConfig returns the decoded MacieConfigResponse (linkingRegions is a
// []string), but tolerate a raw JSON string too.
const toRegionArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

export default function MacieSettingsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [discovery, setDiscovery] = useState(false);
  const [regions, setRegions] = useState([]);
  const [confirm, setConfirm] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const c = await getMacieConfig();
        const cfg = c?.data;
        setConfig(cfg);
        if (cfg) {
          setDiscovery(!!cfg.autoDiscoveryEnabled);
          setRegions(toRegionArray(cfg.linkingRegions));
        }
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveDiscovery = async () => {
    setBusy(true);
    try {
      await setMacieDiscovery(discovery);
      setErr(null);
      // Hand off to the Dashboard entry — it polls config and renders the
      // in-progress banner while discovery is (re)configured across regions.
      navigate('/securityhub/macie');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveRegions = async () => {
    if (!regions.includes(config.homeRegion)) {
      setErr('Home region cannot be removed');
      return;
    }
    setBusy(true);
    try {
      await updateMacieRegions(regions);
      setErr(null);
      // Hand off to the Dashboard entry — it polls config and renders the
      // in-progress banner while regions are enrolled/torn down.
      navigate('/securityhub/macie');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const doDisable = async () => {
    setBusy(true);
    try {
      await disableMacie();
      setConfirmOpen(false);
      setErr(null);
      // Hand off to the Dashboard entry — it polls config and renders the
      // in-progress banner during teardown, then the setup wizard once
      // status transitions to "disabled".
      navigate('/securityhub/macie');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Macie settings…" color={MACIE_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert severity="info" action={
            <Button color="inherit" size="small" onClick={() => navigate('/securityhub/macie')}>
              Go to setup
            </Button>
          }>
            Macie is not set up yet for this customer. Complete the setup wizard first to manage discovery, regions, or disable the service.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
      {err && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={listCardSx}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: `1px solid ${CK.border}`,
            '& .MuiTab-root': {
              fontFamily: FONT_BODY,
              fontWeight: 600,
              fontSize: '0.9rem',
              textTransform: 'none',
              minHeight: 56,
              color: CK.body,
              '&.Mui-selected': { color: MACIE_COLOR },
            },
            '& .MuiTabs-indicator': { backgroundColor: MACIE_COLOR },
          }}
        >
          <Tab label="Discovery" />
          <Tab label="Regions" />
          <Tab label="Permissions" />
          <Tab label="Danger zone" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Automated discovery</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 2 }}>
              Automated sensitive-data discovery lets Macie continuously sample
              objects across your enrolled S3 buckets to surface sensitive data
              and compute per-bucket sensitivity scores.
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Automated sensitive-data discovery is billed by Amazon Macie per GB
              of data inspected. Leave this OFF unless you understand the cost —
              you can enable it at any time and Macie begins sampling on its own
              schedule.
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={discovery}
                  onChange={(e) => setDiscovery(e.target.checked)}
                />
              }
              label="Enable automated sensitive-data discovery"
              sx={{ display: 'block', mb: 1 }}
            />
            <Button
              variant="contained"
              disabled={busy}
              onClick={saveDiscovery}
              sx={{ ...macieButtonSx, mt: 1 }}
            >
              Save discovery setting
            </Button>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Linking regions</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              Home region: <b>{config.homeRegion}</b> (read-only)
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted, mb: 2 }}>
              Adding a region enables Macie for every enrolled member account
              there; removing a region tears down its Macie sessions and prunes
              its findings.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <MultiSelectField
                label="Linking regions"
                options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
                value={regions}
                onChange={setRegions}
                lockedValues={config.homeRegion ? [config.homeRegion] : []}
              />
            </Box>
            <Button variant="contained" disabled={busy} onClick={saveRegions} sx={macieButtonSx}>
              Save regions
            </Button>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <RolePermissionsPanel
              startScan={startMacieRolesScan}
              getJob={getMacieRolesJob}
              fix={fixMacieRoles}
              capabilityLabel="Macie"
            />
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Disable Macie</Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Disabling tears down all member enrollments and Macie sessions in
              every region. Cached findings and the bucket inventory are removed
              on a clean disable.
            </Alert>
            <Button
              color="error"
              variant="contained"
              onClick={() => setConfirmOpen(true)}
              sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
            >
              Disable Macie
            </Button>
          </Box>
        )}
      </Paper>
      </motion.div>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Confirm disable</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1 }}>
            Type the customer ID <b>{config.customerId}</b> to confirm:
          </Typography>
          <TextField
            fullWidth
            label="Customer ID"
            placeholder={config.customerId}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy || confirm !== config.customerId}
            onClick={doDisable}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
